'use client';

// This multi-select input is from https://wds-shadcn-registry.netlify.app/components/multi-select/.
// TODO: replace this component with the new Base UI shadcn/ui multi-select.

import { CheckIcon, ChevronsUpDownIcon, XIcon } from 'lucide-react';
import {
  cloneElement,
  type ComponentPropsWithoutRef,
  createContext,
  Fragment,
  isValidElement,
  type ReactNode,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface MultiSelectContextType {
  items: Map<string, ReactNode>;
  onItemAdded: (value: string, label: ReactNode) => void;
  open: boolean;
  registerItems: boolean;
  selectedValues: Set<string>;
  setOpen: (open: boolean) => void;
  toggleValue: (value: string) => void;
}
const MultiSelectContext = createContext<MultiSelectContextType | null>(null);

export function MultiSelect({
  children,
  defaultValues,
  onOpenChange,
  onValueRemoved,
  onValuesChange,
  registerItems = true,
  values
}: {
  children: ReactNode;
  defaultValues?: string[];
  onOpenChange?: (open: boolean) => void;
  onValueRemoved?: (value: string) => void;
  onValuesChange?: (values: string[]) => void;
  registerItems?: boolean;
  values?: string[];
}) {
  const [open, setOpen] = useState(false);
  const [internalValues, setInternalValues] = useState(
    () => new Set<string>(values ?? defaultValues)
  );
  const selectedValues = values ? new Set(values) : internalValues;
  const [items, setItems] = useState<Map<string, ReactNode>>(() => new Map());

  function toggleValue(value: string) {
    const isRemoving = selectedValues.has(value);
    const getNewSet = (prev: Set<string>) => {
      const newSet = new Set(prev);
      if (newSet.has(value)) {
        newSet.delete(value);
      } else {
        newSet.add(value);
      }
      return newSet;
    };
    setInternalValues(getNewSet);
    onValuesChange?.([...getNewSet(selectedValues)]);
    if (isRemoving) {
      onValueRemoved?.(value);
    }
  }

  const onItemAdded = useCallback(
    (value: string, label: ReactNode) => {
      if (!registerItems) return;
      setItems(prev => {
        if (prev.get(value) === label) return prev;
        return new Map(prev).set(value, label);
      });
    },
    [registerItems]
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [onOpenChange]
  );

  return (
    <MultiSelectContext
      value={{
        items,
        onItemAdded,
        open,
        registerItems,
        selectedValues,
        setOpen,
        toggleValue
      }}
    >
      <Popover
        modal={true}
        onOpenChange={handleOpenChange}
        open={open}
      >
        {children}
      </Popover>
    </MultiSelectContext>
  );
}

export function MultiSelectContent({
  children,
  onSearchChange,
  search = true,
  sortSelectedFirst = true,
  ...props
}: {
  children: ReactNode;
  onSearchChange?: (value: string) => void;
  search?: { emptyMessage?: string; placeholder?: string } | boolean;
  sortSelectedFirst?: boolean;
} & Omit<ComponentPropsWithoutRef<typeof Command>, 'children'>) {
  const { registerItems, selectedValues } = useMultiSelectContext();
  const canSearch = typeof search === 'object' ? true : search;
  // Disable client-side filtering when using server-side search (onSearchChange provided)
  const shouldFilter = onSearchChange ? false : true;

  const sortedChildren = useMemo(() => {
    if (!sortSelectedFirst) return children;
    return sortChildrenBySelected(children, selectedValues);
  }, [children, selectedValues, sortSelectedFirst]);

  return (
    <>
      {registerItems ? (
        <div style={{ display: 'none' }}>
          <Command>
            <CommandList>{children}</CommandList>
          </Command>
        </div>
      ) : null}
      <PopoverContent className="w-auto min-w-[var(--radix-popover-trigger-width)] p-0">
        <Command
          shouldFilter={shouldFilter}
          {...props}
        >
          {canSearch ? (
            <CommandInput
              onValueChange={onSearchChange}
              placeholder={
                typeof search === 'object' ? search.placeholder : undefined
              }
            />
          ) : (
            <button
              autoFocus
              className="sr-only"
            />
          )}
          <CommandList>
            {canSearch && (
              <CommandEmpty>
                {typeof search === 'object' ? search.emptyMessage : undefined}
              </CommandEmpty>
            )}
            {sortedChildren}
          </CommandList>
        </Command>
      </PopoverContent>
    </>
  );
}

export function MultiSelectGroup(
  props: ComponentPropsWithoutRef<typeof CommandGroup>
) {
  return <CommandGroup {...props} />;
}

export function MultiSelectItem({
  badgeLabel,
  children,
  onSelect,
  value,
  ...props
}: {
  badgeLabel?: ReactNode;
  value: string;
} & Omit<ComponentPropsWithoutRef<typeof CommandItem>, 'value'>) {
  const { onItemAdded, registerItems, selectedValues, toggleValue } =
    useMultiSelectContext();
  const isSelected = selectedValues.has(value);

  useEffect(() => {
    if (!registerItems) return;
    onItemAdded(value, badgeLabel ?? children);
  }, [value, children, onItemAdded, badgeLabel, registerItems]);

  return (
    <CommandItem
      value={value}
      {...props}
      onSelect={() => {
        toggleValue(value);
        onSelect?.(value);
      }}
    >
      {isSelected ? (
        <div className="bg-primary relative size-4 rounded">
          <CheckIcon
            absoluteStrokeWidth
            className="absolute inset-0 m-auto text-white"
            size={14}
            strokeWidth={1.33}
          />
        </div>
      ) : (
        <div className="border-input size-4 rounded border" />
      )}
      {children}
    </CommandItem>
  );
}

export function MultiSelectSeparator(
  props: ComponentPropsWithoutRef<typeof CommandSeparator>
) {
  return <CommandSeparator {...props} />;
}

export function MultiSelectTrigger({
  children,
  className,
  ...props
}: {
  children?: ReactNode;
  className?: string;
} & ComponentPropsWithoutRef<typeof Button>) {
  const { open } = useMultiSelectContext();

  return (
    <PopoverTrigger asChild>
      <Button
        {...props}
        aria-expanded={props['aria-expanded'] ?? open}
        className={cn(
          "border-input focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 data-[placeholder]:text-muted-foreground dark:bg-input/30 dark:hover:bg-input/50 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='text-'])]:text-muted-foreground flex h-auto min-h-9 w-fit items-center justify-between gap-2 overflow-hidden rounded-md border bg-transparent px-3 py-1.5 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
          className
        )}
        role={props.role ?? 'combobox'}
        variant={props.variant ?? 'outline'}
      >
        {children}
        <ChevronsUpDownIcon className="size-4 shrink-0 opacity-50" />
      </Button>
    </PopoverTrigger>
  );
}

export function MultiSelectValue({
  className,
  clickToRemove = true,
  overflowBehavior = 'wrap-when-open',
  placeholder,
  ...props
}: {
  clickToRemove?: boolean;
  overflowBehavior?: 'cutoff' | 'wrap-when-open' | 'wrap';
  placeholder?: string;
} & Omit<ComponentPropsWithoutRef<'div'>, 'children'>) {
  const { items, open, selectedValues, toggleValue } = useMultiSelectContext();
  const [overflowAmount, setOverflowAmount] = useState(0);
  const valueRef = useRef<HTMLDivElement>(null);
  const overflowRef = useRef<HTMLDivElement>(null);

  const shouldWrap =
    overflowBehavior === 'wrap' ||
    (overflowBehavior === 'wrap-when-open' && open);
  const selectedValuesOverflowKey = useMemo(
    () => [...selectedValues].sort().join('|'),
    [selectedValues]
  );

  const checkOverflow = useCallback(() => {
    if (valueRef.current == null) return;

    const containerElement = valueRef.current;
    const overflowElement = overflowRef.current;
    const items = containerElement.querySelectorAll<HTMLElement>(
      '[data-selected-item]'
    );

    if (overflowElement != null) overflowElement.style.display = 'none';
    items.forEach(child => child.style.removeProperty('display'));
    let amount = 0;
    for (let i = items.length - 1; i >= 0; i--) {
      const child = items[i];
      if (containerElement.scrollWidth <= containerElement.clientWidth) {
        break;
      }
      amount = items.length - i;
      child.style.display = 'none';
      overflowElement?.style.removeProperty('display');
    }
    setOverflowAmount(amount);
  }, []);

  const handleResize = useCallback(
    (node: HTMLDivElement) => {
      valueRef.current = node;

      const observer = new ResizeObserver(checkOverflow);
      observer.observe(node);

      return () => {
        observer.disconnect();
        valueRef.current = null;
      };
    },
    [checkOverflow]
  );

  useEffect(() => {
    const containerElement = valueRef.current;
    if (containerElement == null) return;

    const rafId = requestAnimationFrame(() => {
      const selectedItems = containerElement.querySelectorAll<HTMLElement>(
        '[data-selected-item]'
      );
      selectedItems.forEach(item => item.style.removeProperty('display'));

      if (shouldWrap) {
        overflowRef.current?.style.setProperty('display', 'none');
        setOverflowAmount(0);
        return;
      }

      checkOverflow();
    });

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [checkOverflow, items, selectedValuesOverflowKey, shouldWrap]);

  if (selectedValues.size === 0 && placeholder) {
    return (
      <span className="text-muted-foreground min-w-0 overflow-hidden font-normal">
        {placeholder}
      </span>
    );
  }

  return (
    <div
      {...props}
      className={cn(
        'flex w-fit gap-1.5 overflow-hidden',
        shouldWrap && 'h-full flex-wrap',
        className
      )}
      ref={handleResize}
    >
      {[...selectedValues]
        .filter(value => items.has(value))
        .map(value => (
          <Badge
            className="group flex items-center gap-1"
            data-selected-item
            key={value}
            onClick={
              clickToRemove
                ? e => {
                    e.stopPropagation();
                    toggleValue(value);
                  }
                : undefined
            }
            variant="outline"
          >
            {items.get(value)}
            {clickToRemove && (
              <XIcon className="text-muted-foreground group-hover:text-destructive size-2" />
            )}
          </Badge>
        ))}
      <Badge
        ref={overflowRef}
        style={{
          display: overflowAmount > 0 && !shouldWrap ? 'block' : 'none'
        }}
        variant="outline"
      >
        +{overflowAmount}
      </Badge>
    </div>
  );
}

/**
 * Flattens React children, expanding fragments and arrays into their children
 */
function flattenChildren(children: ReactNode): ReactNode[] {
  const result: ReactNode[] = [];

  if (children == null) {
    return result;
  }

  // Handle arrays (from .map() calls)
  if (Array.isArray(children)) {
    for (const child of children as ReactNode[]) {
      result.push(...flattenChildren(child));
    }
    return result;
  }

  // Check if this is a React Fragment
  if (
    isValidElement<{ children?: ReactNode }>(children) &&
    children.type === Fragment
  ) {
    // Recursively flatten fragment children
    result.push(...flattenChildren(children.props.children));
    return result;
  }

  // Regular element or primitive
  result.push(children);
  return result;
}

function sortChildrenBySelected(
  children: ReactNode,
  selectedValues: Set<string>
): ReactNode[] {
  // Convert children to array for processing
  const childArray: ReactNode[] = Array.isArray(children)
    ? (children as ReactNode[])
    : [children];

  return childArray.map((child): ReactNode => {
    // Check if this is a valid React element with children (like MultiSelectGroup)
    if (
      isValidElement<{ children?: ReactNode }>(child) &&
      child.props.children != null
    ) {
      // Flatten children to handle fragments
      const flatChildren = flattenChildren(child.props.children);

      // Sort items while preserving non-item elements (headers, divs) in their positions
      // Items are sorted within sections separated by non-items
      const result: ReactNode[] = [];
      let pendingSelected: ReactNode[] = [];
      let pendingUnselected: ReactNode[] = [];

      for (const item of flatChildren) {
        if (isValidElement<{ value?: string }>(item) && item.props.value) {
          // This is a selectable item
          const value = item.props.value;
          if (selectedValues.has(value)) {
            pendingSelected.push(item);
          } else {
            pendingUnselected.push(item);
          }
        } else {
          // Non-item element (header, div, etc.) - flush pending items and add this element
          result.push(...pendingSelected, ...pendingUnselected);
          pendingSelected = [];
          pendingUnselected = [];
          result.push(item);
        }
      }

      // Flush any remaining items
      result.push(...pendingSelected, ...pendingUnselected);

      // Generate a key based on selected values to force re-render when selection changes
      const selectedKey = [...selectedValues].sort().join(',');

      // eslint-disable-next-line react-x/no-clone-element -- Required for sorting children
      return cloneElement(child, {
        children: result,
        key: `group-${selectedKey}`
      });
    }
    return child;
  });
}

function useMultiSelectContext() {
  const context = use(MultiSelectContext);
  if (context == null) {
    throw new Error(
      'useMultiSelectContext must be used within a MultiSelectContext'
    );
  }
  return context;
}
